import { createBrowserRouter, RouterProvider } from "react-router-dom";

import AccountSettingsPage from "./Routes/settings/account";
import AppLayout from "./Blocks/Composites/AppLayout/AppLayout";
import ChartSettingsPage from "./Routes/settings/chart";
import DetailsPage from "./Routes/details";
import IndexPage from "./Routes/index";
import Providers from "@/providers/Providers";
import TabularDisplayProvider from "@/providers/TabularDisplayProvider";
import PlanSettingsPage from "./Routes/settings/plan";
import { PermissionGate } from "./PermissionGate";
import { PlanSettingsProvider } from "@/providers/PlanSettingsProvider";
import { appCanvasRoutes } from "@coreapp/constants";
import { routePatterns } from "./routes";

export { appCanvasRoutes };

const canvasElement = (
  <TabularDisplayProvider>
    <IndexPage />
  </TabularDisplayProvider>
);

const routes = [
  {
    element: (
      <Providers>
        <AppLayout />
      </Providers>
    ),
    children: [
      // All canvas routes wrap canvasElement in PermissionGate — even
      // the ones with no permission check (default true) — so React
      // Router sees the same element type across route matches and
      // reconciles without unmounting IndexPage. Mismatched wrapping
      // would unmount/remount on every cross-route nav (e.g.
      // /base/ ↔ /plan/), resetting useDocumentLoader's refs and
      // swallowing in-session doc switches silently.
      {
        path: routePatterns.base,
        element: <PermissionGate check={() => true}>{canvasElement}</PermissionGate>
      },
      {
        path: routePatterns.open,
        element: <PermissionGate check={() => true}>{canvasElement}</PermissionGate>
      },
      {
        path: routePatterns.plan,
        element: (
          <PermissionGate check={perms => !!perms.canAccessPlanning()}>
            {canvasElement}
          </PermissionGate>
        )
      },
      {
        path: routePatterns.boxlayout,
        element: (
          <PermissionGate check={perms => !!perms.canEditDocuments()}>
            {canvasElement}
          </PermissionGate>
        )
      },
      ...appCanvasRoutes.map(path => ({
        path: `${path}/:subModeOrId?/:id?`,
        element: <PermissionGate check={() => true}>{canvasElement}</PermissionGate>
      })),
      {
        path: routePatterns.chartSettings,
        element: (
          <PermissionGate check={perms => !!perms.isAdmin()}>
            <ChartSettingsPage />
          </PermissionGate>
        )
      },
      {
        path: routePatterns.accountSettings,
        element: (
          <PermissionGate check={perms => !!perms.isAdmin()}>
            <AccountSettingsPage />
          </PermissionGate>
        )
      },
      {
        path: routePatterns.planSettings,
        element: (
          <PermissionGate check={perms => !!perms.isAdmin()}>
            <PlanSettingsProvider>
              <PlanSettingsPage />
            </PlanSettingsProvider>
          </PermissionGate>
        )
      },
      { path: routePatterns.details, element: <DetailsPage /> }
    ]
  }
];

const router = createBrowserRouter(routes, {
  basename: "/app"
});

const Root = () => {
  // v7 wraps router state updates in React.startTransition by default; v6 never did.
  return <RouterProvider router={router} useTransitions={false} />;
};

export default Root;
